import { useCallback, useEffect, useState } from "react";
import { IFRAME_ID } from "../constants/privacyPolicyConstants";
import { applyIframeTheme, setIframeHeight } from "../utils/privacyPolicyUtils";

interface UsePrivacyPolicyIframeReturn {
  isIframeLoaded: boolean;
  iframeLoaded: () => void;
  triggerIframeReflow: () => void;
}

/**
 * Custom hook for managing privacy policy iframe
 * Handles iframe loading, dark mode application, and resize handling
 */
export const usePrivacyPolicyIframe = (
  isDarkMode: boolean
): UsePrivacyPolicyIframeReturn => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  /**
   * Handle iframe load event
   * Applies dark mode theme and sets iframe height
   */
  const iframeLoaded = useCallback(() => {
    const iFrameID = document.getElementById(
      IFRAME_ID
    ) as HTMLIFrameElement | null;

    if (!iFrameID?.contentWindow || !iFrameID.contentDocument) {
      return;
    }

    const doc = iFrameID.contentDocument;

    // Apply dark mode theme to iframe
    applyIframeTheme(doc, isDarkMode);

    // Set iframe height based on content
    setIframeHeight(doc, iFrameID);
  }, [isDarkMode]);

  /**
   * Trigger iframe reflow for initial load
   * Used to handle delayed layout calculation on page refresh
   */
  const triggerIframeReflow = useCallback(() => {
    setIsIframeLoaded(true);
  }, []);

  // Set up resize listener for iframe height adjustment
  useEffect(() => {
    iframeLoaded();
    window.addEventListener("resize", iframeLoaded);

    return () => {
      window.removeEventListener("resize", iframeLoaded);
    };
  }, [iframeLoaded]);

  return {
    isIframeLoaded,
    iframeLoaded,
    triggerIframeReflow,
  };
};
